/**
 * CDB54 Calendrier+ - API Service
 * Centralized API client configuration with axios
 */

import axios, { AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import type { ApiError } from '@/types/models';

// Create axios instance with default configuration
const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true, // Required for Sanctum SPA authentication
});

// Request interceptor for CSRF token
api.interceptors.request.use(async (config) => {
  // Get CSRF token from cookie for non-GET requests
  if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() ?? '')) {
    const xsrfToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];

    if (xsrfToken) {
      config.headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
    }
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiError>) => {
    // Handle 401 Unauthorized - redirect to login ONLY for protected endpoints
    // Skip redirect for auth check endpoints (used by public pages)
    if (error.response?.status === 401) {
      const url = error.config?.url ?? '';
      // Don't redirect for auth check or public API endpoints
      const isAuthCheck = url.includes('/auth/me') || url.includes('/auth/refresh');
      const isPublicEndpoint = url.includes('/public/');

      if (!isAuthCheck && !isPublicEndpoint && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Initialize CSRF protection by fetching Sanctum CSRF cookie
 */
export async function initCsrf(): Promise<void> {
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true });
}

export default api;
